import React, { useEffect, useRef, useState } from "react";
import { Sun, Moon, MonitorSmartphone, Clock3, Rows3, Rows4, Check, Volume2, BellRing, Home, Play, ALargeSmall, Type, Contrast, CircleDot } from "lucide-react";
import { useAppearance } from "../lib/theme";
import { getNotifySettings, setNotifySettings, ensureDesktopPermission } from "../lib/notify";
import { chime } from "../lib/beep";
import { toast } from "./ui/sonner";

const MODES = [
  { k: "light", icon: Sun, zh: "浅色", en: "Light" },
  { k: "dark", icon: Moon, zh: "深色", en: "Dark" },
  { k: "system", icon: MonitorSmartphone, zh: "跟随系统", en: "System" },
  { k: "schedule", icon: Clock3, zh: "定时", en: "Scheduled" },
];

const Opt = ({ active, onClick, icon: Icon, label, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} data-active={active ? "1" : "0"}
    className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium transition-colors ${active ? "border-brand-300 bg-brand-50 text-brand-600" : "border-slate-200 text-slate-600 hover:border-brand-200 hover:bg-slate-50"}`}>
    <Icon className="h-3.5 w-3.5" /><span className="flex-1 text-left">{label}</span>{active && <Check className="h-3.5 w-3.5" />}
  </button>
);

// Appearance / density / notifications / landing controls (shared by the desktop popover and the mobile more-sheet)
export function DisplayPanel({ zh, onSave, usingDefaults = false, landing, landingOptions = [], onLanding }) {
  const [settings, apply] = useAppearance();
  const [notify, setNotify] = useState(getNotifySettings);
  useEffect(() => { const h = (e) => setNotify(e.detail); window.addEventListener("ali-notify", h); return () => window.removeEventListener("ali-notify", h); }, []);
  const toggleSound = () => { const s = setNotifySettings({ sound: !notify.sound }); onSave?.({ sound: s.sound }); if (s.sound) chime(); };
  const toggleDesktop = async () => {
    if (notify.desktop) { setNotifySettings({ desktop: false }); onSave?.({ desktopNotify: false }); return; }
    const perm = await ensureDesktopPermission();
    if (perm !== "granted") { toast.error(perm === "unsupported" ? (zh ? "当前浏览器不支持桌面通知" : "Desktop notifications not supported") : (zh ? "浏览器已拒绝通知权限，请在地址栏站点设置中允许" : "Notification permission denied — allow it in the browser site settings")); return; }
    setNotifySettings({ desktop: true }); onSave?.({ desktopNotify: true });
    toast.success(zh ? "已开启桌面通知（切到其他窗口时推送）" : "Desktop notifications on (shown when this tab is in the background)");
  };
  const setMode = (mode) => { apply({ mode }); onSave?.({ theme: mode }); };
  const setSched = (k, v) => { const schedule = { ...settings.schedule, [k]: v }; apply({ schedule }); onSave?.(k === "from" ? { darkFrom: v } : { darkTo: v }); };
  const setDensity = (density) => { apply({ density }); onSave?.({ density }); };
  const setFont = (fontScale) => { apply({ fontScale }); onSave?.({ fontScale }); };
  const setContrast = (contrast) => { apply({ contrast }); onSave?.({ contrast }); };

  return (
    <div data-testid="display-panel">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mb-2">{zh ? "外观" : "Appearance"}</p>
      <div className="grid grid-cols-2 gap-2">
        {MODES.map((m) => <Opt key={m.k} active={settings.mode === m.k} onClick={() => setMode(m.k)} icon={m.icon} label={zh ? m.zh : m.en} testid={`theme-mode-${m.k}`} />)}
      </div>
      {settings.mode === "schedule" && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600 px-1" data-testid="theme-schedule">
          <span className="w-full text-[11px] text-slate-500">{zh ? "深色时段（跨夜自动识别）" : "Dark window (overnight ok)"}</span>
          <input type="time" value={settings.schedule.from} onChange={(e) => setSched("from", e.target.value)} data-testid="theme-sched-from" className="h-8 flex-1 rounded-lg border border-slate-200 px-2 text-xs bg-white" />
          <span>–</span>
          <input type="time" value={settings.schedule.to} onChange={(e) => setSched("to", e.target.value)} data-testid="theme-sched-to" className="h-8 flex-1 rounded-lg border border-slate-200 px-2 text-xs bg-white" />
        </div>
      )}
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mt-4 mb-2">{zh ? "密度" : "Density"}</p>
      <div className="grid grid-cols-2 gap-2">
        <Opt active={settings.density !== "compact"} onClick={() => setDensity("comfortable")} icon={Rows3} label={zh ? "舒适" : "Comfortable"} testid="density-comfortable" />
        <Opt active={settings.density === "compact"} onClick={() => setDensity("compact")} icon={Rows4} label={zh ? "紧凑" : "Compact"} testid="density-compact" />
      </div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mt-4 mb-2">{zh ? "字号" : "Font size"}</p>
      <div className="grid grid-cols-2 gap-2" data-testid="font-scale-group">
        <Opt active={settings.fontScale !== "large"} onClick={() => setFont("standard")} icon={ALargeSmall} label={zh ? "标准" : "Standard"} testid="font-standard" />
        <Opt active={settings.fontScale === "large"} onClick={() => setFont("large")} icon={Type} label={zh ? "大号 (+12%)" : "Large (+12%)"} testid="font-large" />
      </div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mt-4 mb-2">{zh ? "对比度" : "Contrast"}</p>
      <div className="grid grid-cols-2 gap-2" data-testid="contrast-group">
        <Opt active={settings.contrast !== "high"} onClick={() => setContrast("standard")} icon={CircleDot} label={zh ? "标准" : "Standard"} testid="contrast-standard" />
        <Opt active={settings.contrast === "high"} onClick={() => setContrast("high")} icon={Contrast} label={zh ? "高对比" : "High"} testid="contrast-high" />
      </div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mt-4 mb-2">{zh ? "提醒通知" : "Notifications"}</p>
      <div className="grid grid-cols-2 gap-2">
        <Opt active={notify.sound} onClick={toggleSound} icon={Volume2} label={zh ? "提示音" : "Chime"} testid="notify-sound-toggle" />
        <Opt active={notify.desktop} onClick={toggleDesktop} icon={BellRing} label={zh ? "桌面通知" : "Desktop"} testid="notify-desktop-toggle" />
      </div>
      <button type="button" onClick={() => chime()} data-testid="notify-sound-test" className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-brand-600 px-1"><Play className="h-3 w-3" />{zh ? "试听提示音" : "Preview chime"}</button>
      {landingOptions.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mt-4 mb-2">{zh ? "登录后打开" : "Landing page"}</p>
          <div className="flex items-center gap-2 px-1">
            <Home className="h-4 w-4 text-slate-400 shrink-0" />
            <select value={landing || ""} onChange={(e) => onLanding?.(e.target.value || null)} data-testid="landing-select" className="flex-1 h-9 rounded-xl border border-slate-200 bg-white px-2 text-xs text-slate-700">
              <option value="">{zh ? "角色默认" : "Role default"}</option>
              {landingOptions.map((o) => <option key={o.to} value={o.to}>{o.label}</option>)}
            </select>
          </div>
        </>
      )}
      <p className="text-[11px] text-slate-400 px-1 mt-3">{zh ? "紧凑模式缩小表格行高与卡片留白；大号字体整体放大约 12%；高对比加深正文、加实边框。所有设置会同步到你的账号。" : "Compact tightens rows and padding; Large scales text ~12%; High contrast darkens text and strengthens borders. All settings sync to your account."}</p>
      {usingDefaults && <p className="text-[11px] text-brand-600 px-1 mt-1" data-testid="display-menu-defaults-hint">{zh ? "当前沿用公司默认外观，修改后将以你的个人设置为准。" : "Currently using the company default — any change becomes your personal setting."}</p>}
    </div>
  );
}

// Topbar popover wrapper (desktop / tablet)
export function DisplayMenu({ zh, theme, ...panel }) {
  const [open, setOpen] = useState(false);
  const [settings] = useAppearance();
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="theme-toggle" data-theme={theme} data-mode={settings.mode}
        className="h-9 w-9 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
        title={zh ? "外观与密度" : "Appearance & density"}>
        {theme === "dark" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 max-h-[80vh] overflow-y-auto rounded-2xl border border-slate-200/80 bg-white shadow-lift p-3 fade-up" data-testid="display-menu">
          <DisplayPanel zh={zh} {...panel} />
        </div>
      )}
    </div>
  );
}

export default DisplayMenu;
